import { Switch } from '@material-ui/core';
import { Tooltip } from '@material-ui/core';
import i18next from 'i18next';
import moment from 'moment';
import React from 'react';

import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import {
  CalendarIcon,
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, spacing, Text, usePanel } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { DateRange, defaultDateRanges, IDateRange } from '~/src/dash/utils/dateTimeRanges';
import { DashboardTrendsSticker } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { usePublicaTranslation } from '~/src/dash/publicaUI';

interface IDashboardCompareToggleProps {
  isCompareEnabled: boolean;
  setIsCompareEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const excludeTodayEnabledDateRangeKeys = ['Last 7 Days', 'Last 30 Days'];

const DashboardCompareToggle = ({
  isCompareEnabled,
  setIsCompareEnabled,
}: IDashboardCompareToggleProps) => {
  return (
    <div onClick={() => setIsCompareEnabled(!isCompareEnabled)}>
      <Cols style={{ alignItems: 'center', cursor: 'pointer' }} gap='4px'>
        <Col displayFlex>
          {isCompareEnabled ? (
            <CheckboxCheckedIcon color={publicaColors.darkGray} />
          ) : (
            <CheckboxUncheckedIcon color={publicaColors.darkGray} />
          )}
        </Col>
        <Col>
          <DashboardTrendsSticker isActive={isCompareEnabled} />
        </Col>
        <Col>
          <Text small bold disableSelect tKey='Compare'>
            Compare
          </Text>
        </Col>
      </Cols>
    </div>
  );
};

interface IDashboardPeriodsProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calCompareStartDate: moment.Moment;
  calCompareEndDate: moment.Moment;
  isExcludingToday: boolean;
  handleExcludeTodayToggle: () => void;
  isCompareEnabled: boolean;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  handleApplyCompareDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  dateRange: IDateRange;
  handleToggleDateRange: (rangeKey: DateRange) => void;
  setIsCompareEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled?: boolean;
  isLoading: boolean;
  timezone: string;
}

export const DashboardPeriods = ({
  calStartDate,
  calEndDate,
  calCompareStartDate,
  calCompareEndDate,
  isExcludingToday,
  isLoading,
  handleExcludeTodayToggle,
  isCompareEnabled,
  handleApplyDatesChange,
  handleApplyCompareDatesChange,
  dateRange,
  handleToggleDateRange,
  setIsCompareEnabled,
  isDisabled,
  timezone,
}: IDashboardPeriodsProps) => {
  const { height } = usePanel();
  const { t } = usePublicaTranslation();

  const isExcludeTodayToggleDisabled =
    !excludeTodayEnabledDateRangeKeys.includes(dateRange.key) || isLoading;
  const getExcludeTodayToggleDisabledTooltip = (): string => {
    if (isLoading) {
      return t('LoadingEllipsis');
    }
    return t('DashboardTooltipExcludeToday');
  };

  return (
    <Cols extraSmallVerticalPadding gap={spacing.extraLarge} style={{ alignItems: 'center' }}>
      <Col>
        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
          <Col displayFlex>
            <CalendarIcon color={publicaColors.veryDarkGray} style={{ height: '14px' }} />
          </Col>
          <Col>
            <Text small bold tKey='Period'>
              Period
            </Text>
          </Col>
        </Cols>
      </Col>
      <Col>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center', rowGap: spacing.extraSmall }}>
          <Col className='calendar_input_wrapper'>
            <CalendarV2
              calStartDate={calStartDate}
              calEndDate={calEndDate}
              handleApplyDatesChange={handleApplyDatesChange}
              isDisabled={isDisabled}
              uniqueID='mainCal'
            />
          </Col>
          <Col>
            <DateTimeRangeToggles
              ranges={defaultDateRanges}
              activeKey={dateRange.key}
              onClick={handleToggleDateRange}
            />
          </Col>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <Tooltip title={getExcludeTodayToggleDisabledTooltip()}>
              <div>
                <Switch
                  checked={isExcludingToday}
                  disabled={isExcludeTodayToggleDisabled}
                  onChange={handleExcludeTodayToggle}
                  name='ExcludeToday'
                  size='small'
                />
              </div>
            </Tooltip>
            <Text bold small tKey='ExcludeToday'>
              Exclude Today
            </Text>
          </Col>
          <Col>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <DashboardCompareToggle
                  isCompareEnabled={isCompareEnabled}
                  setIsCompareEnabled={setIsCompareEnabled}
                />
              </Col>
              <Col className='calendar_input_wrapper'>
                <CalendarV2
                  calStartDate={calCompareStartDate}
                  calEndDate={calCompareEndDate}
                  handleApplyDatesChange={handleApplyCompareDatesChange}
                  isCompareCalendar
                  endDateOffset={calEndDate
                    .clone()
                    .tz(timezone)
                    .endOf('day')
                    .diff(calStartDate.clone().tz(timezone).startOf('day'), 'days')}
                  maxDate={calStartDate.clone()}
                  isDisabled={isDisabled || !isCompareEnabled}
                  anchorDirection={(height || 0) > 64 ? 'left' : 'right'} // a height increase means a row break has occurred and the compare calendar input is now on the left and must change accordingly
                  uniqueID='compareCal'
                  language={i18next.language}
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Col>
    </Cols>
  );
};
